import HowToRegIcon from '@mui/icons-material/HowToReg';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Box, Checkbox, CircularProgress, Menu, MenuItem, TextField, Tooltip } from '@mui/material';
import { ChangeEvent, FC, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { DeliveryInspectionStage, DeliveryStatus } from 'api/models/Deliveries';
import { Set } from 'api/models/Sets';
import theme from 'App.theme';
import { SetButton } from 'components/SetButton/SetButton';
import { DeliveryChip } from 'components/DeliveryChip';
import TrayLabels from 'components/TrayLabels/TrayLabels';
import { useLanguage } from 'context/AppContext';
import { TurnaroundDeliveryChip } from 'components/TurnaroundDeliveryChip';
import useSetDisplayLabel from 'hooks/useSetDisplayLabel';

type TSetButton = FC<{
  stage: DeliveryInspectionStage;
  deliveryStatus: DeliveryStatus;
  supplierCreated?: boolean;
  sets?: Set[];
  set: Set;
  tempNotified?: boolean;
  disabled?: boolean;
  editable?: boolean;
  selected?: boolean;
  showCheckbox?: boolean; // multi-select mode: swaps the status icon for a checkbox reflecting `selected`
  onClick?: () => void;
  onChangeSetDisplayCode?: (kitInstanceId: string, newSetDisplayCode: string) => void;
  openNotificationModal?: VoidFunction;
  openDeleteModal?: VoidFunction;
  onUngroup?: VoidFunction; // consolidated sets only; menu item is hidden once the set is checked in
  ungroupLoading?: boolean;
}>;

const DeliverySetButton: TSetButton = ({
  stage,
  deliveryStatus,
  supplierCreated = false,
  sets,
  set,
  tempNotified,
  disabled,
  editable,
  selected = false,
  showCheckbox = false,
  onClick,
  onChangeSetDisplayCode,
  openNotificationModal,
  openDeleteModal,
  onUngroup,
  ungroupLoading,
}) => {
  const lang = useLanguage()[0];

  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(menuAnchorEl);

  const setDisplayLabel = useSetDisplayLabel(set);

  const [displayCode, setDisplayCode] = useState(setDisplayLabel);

  // update the displayCode if it changes externally
  useEffect(() => {
    setDisplayCode(setDisplayLabel);
  }, [setDisplayLabel]);

  const isPendingTurnaround = Boolean(set.turnaroundDestination && set?.turnaroundStage == 'PENDING');
  const isConfirmed = Boolean(set?.inspections && set.inspections[stage] == 'CONFIRMED');
  const isCheckOut = stage == 'CHECK_OUT';
  const apiResponseNotified = set.notificationsSent?.checkout;

  const isPastDelivery = deliveryStatus == 'CHECKED_OUT' || deliveryStatus == 'DELETED' || deliveryStatus == 'CANCELED';
  const quickCheckedOutReason = set.quickCheckedOut?.reason;
  const showNotificationBtn =
    !!openNotificationModal &&
    !isPastDelivery &&
    isCheckOut &&
    isConfirmed &&
    !quickCheckedOutReason &&
    !(tempNotified || apiResponseNotified) &&
    !supplierCreated;

  const isSurgeryPage = location.pathname.startsWith('/surgery/');
  const isOrderPage = location.pathname.startsWith('/orders/');
  const showDeleteBtn =
    !!openDeleteModal &&
    !isSurgeryPage &&
    !isOrderPage &&
    !isCheckOut &&
    !isConfirmed &&
    !supplierCreated &&
    // deleting either side would orphan the group
    !set.consolidatedSet &&
    !set.consolidatedIntoKitInstanceId;

  const isDispatchedOrder = deliveryStatus == 'OUTBOUND';
  const showUngroupBtn = !!onUngroup && !!set.consolidatedSet && set.inspections?.CHECK_IN?.status != 'CONFIRMED';
  const showMenu = (showNotificationBtn || showDeleteBtn || showUngroupBtn) && !isDispatchedOrder && !supplierCreated;

  // a member's status belongs to its consolidated set, so it never carries turnaround chips
  const isTurnaroundSetOrigin = set.turnaroundOrigin && !set.consolidatedIntoKitInstanceId;
  const isTurnaroundSetDestination = set.turnaroundDestination && !set.consolidatedIntoKitInstanceId;

  const trays = useMemo(() => set.trays.filter(tray => tray.name) || [], [set.trays]);

  const onMenuClick = useCallback((event: React.MouseEvent<HTMLOrSVGElement>) => {
    // don't let the click bubble up to the container button
    event.preventDefault();
    event.stopPropagation();

    setMenuAnchorEl(event.currentTarget as HTMLElement); //  HTMLOrSVGElement is practically HTMLElement
  }, []);

  const onMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  const handleChangeSetDisplayCode = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const newSetDisplayCode = event.target.value;

    setDisplayCode(newSetDisplayCode);
  }, []);

  const onBlur = useCallback(() => {
    // validate
    if (!displayCode) {
      toast.warn(`${lang.DeliverySetButton.setNameRequired}`);

      // reset to the original value
      setDisplayCode(setDisplayLabel);

      return;
    }

    const setDisplayCodeExists = sets
      ?.filter(s => s.kitInstanceId !== set.kitInstanceId)
      .some(s => s.displayCode == displayCode);

    if (setDisplayCodeExists) {
      toast.warn(
        `${lang.DeliverySetButton.setNameExists({
          newDisplayCode: displayCode,
          originalDisplayCode: setDisplayLabel,
        })}`,
      );

      // reset to the original value
      setDisplayCode(setDisplayLabel);

      return;
    }

    // call onChangeSetDisplayCode if it's valid
    onChangeSetDisplayCode?.(set.kitInstanceId, displayCode);
  }, [sets, displayCode, lang.DeliverySetButton, onChangeSetDisplayCode, setDisplayLabel, set.kitInstanceId]);

  const haveRow2 = !!(
    trays?.length ||
    isTurnaroundSetOrigin ||
    isTurnaroundSetDestination ||
    set.express ||
    set.consolidatedSet
  );

  return (
    <Box>
      <Tooltip title={quickCheckedOutReason ? lang.QuickCheckOutReasons[quickCheckedOutReason] : ''}>
        {/* this Box is needed to hold the Tooltip ref because SetButton is a ForwardRef */}
        <Box>
          <SetButton
            status={deliveryStatus}
            inspections={set.inspections}
            isPendingTurnaround={isPendingTurnaround}
            showIcon={!showCheckbox}
            disabled={disabled || ungroupLoading}
            disableRipple={editable || isPendingTurnaround}
            onClick={editable || isPendingTurnaround ? undefined : onClick}
            selected={selected}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                gap: theme.layout.spacing.small,
              }}
            >
              {/* SET NAME */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexShrink: 1,
                  minWidth: 0,
                }}
              >
                {showCheckbox && (
                  <Checkbox
                    checked={selected}
                    disableRipple
                    tabIndex={-1}
                    inputProps={{ readOnly: true }}
                    sx={{
                      padding: 0,
                      marginRight: theme.layout.spacing.small,
                      pointerEvents: 'none', // the whole button is the click target
                      color: theme.greys.GREY4,
                      '&.Mui-checked': { color: theme.colors.active },
                    }}
                  />
                )}
                {editable && onChangeSetDisplayCode ? (
                  <TextField
                    value={displayCode}
                    variant="outlined"
                    size="small"
                    sx={{ minWidth: 120 }}
                    onChange={handleChangeSetDisplayCode}
                    onBlur={onBlur}
                  />
                ) : (
                  <div
                    style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      textAlign: 'left',
                      maxWidth: '100%',
                    }}
                  >
                    {setDisplayLabel}
                    {isPastDelivery && set.serial ? ` (${set.serial})` : ''}
                  </div>
                )}
              </div>

              {/* SUFFIX ICONS */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexShrink: 0,
                  minWidth: 0,
                  gap: theme.layout.spacing.small,
                }}
              >
                {quickCheckedOutReason && <HowToRegIcon />}

                {ungroupLoading ? <CircularProgress size={16} /> : showMenu && <MoreHorizIcon onClick={onMenuClick} />}
              </div>
            </div>

            {haveRow2 && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  gap: theme.layout.spacing.small,
                  marginBottom: theme.layout.spacing.tiny,
                }}
              >
                {/* TRAY NAME LABELS */}
                <TrayLabels trays={trays} />

                {/* LABELS */}
                <div
                  style={{
                    display: 'flex',
                    gap: theme.layout.spacing.small,
                    overflowX: 'auto',
                    msOverflowStyle: 'none' /* IE and Edge */,
                    scrollbarWidth: 'none' /* Firefox */,
                  }}
                >
                  {isTurnaroundSetOrigin && (
                    <TurnaroundDeliveryChip
                      variant="turnaroundOrigin"
                      turnaroundDestinationDeliveryId={set.turnaroundDestinationDeliveryId || null}
                    />
                  )}

                  {isTurnaroundSetDestination && (
                    <TurnaroundDeliveryChip
                      variant="turnaroundDestination"
                      turnaroundOriginDeliveryId={set.turnaroundOriginDeliveryId || null}
                    />
                  )}
                  {set.express && <DeliveryChip variant="express">Express</DeliveryChip>}
                  {set.consolidatedSet && (
                    <DeliveryChip
                      variant="consolidated"
                      style={{ gap: theme.layout.spacing.tiny }}
                      aria-label={lang.ConsolidatedSetGroup.consolidated}
                    >
                      <LayersOutlinedIcon sx={{ fontSize: 12 }} />
                      {lang.ConsolidatedSetGroup.consolidated}
                    </DeliveryChip>
                  )}
                </div>
              </div>
            )}
          </SetButton>
        </Box>
      </Tooltip>

      {/* MENU */}
      <Menu anchorEl={menuAnchorEl} open={menuOpen} onClose={onMenuClose}>
        {showUngroupBtn && (
          <MenuItem
            onClick={() => {
              onUngroup();
              onMenuClose();
            }}
          >
            {lang.ConsolidatedSetGroup.ungroup}
          </MenuItem>
        )}
        {showNotificationBtn && (
          <MenuItem
            onClick={() => {
              openNotificationModal();
              onMenuClose();
            }}
          >
            {lang.DeliverySetButton.sendReadinessNotification}
          </MenuItem>
        )}

        {showDeleteBtn && (
          <MenuItem
            onClick={() => {
              openDeleteModal();
            }}
          >
            {lang.DeliverySetButton.deleteSet}
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default DeliverySetButton;
